import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { upsertAppParameters, getAppParameters } from '../helper/Api/AdminApi';

const AppParameters = () => {
  const [formData, setFormData] = useState({
    noOfCompany: '',
    noOfAmwalySelectedCompany: '',
    NoOfCompanyMonetaire: '',
    NoOfAmwalySelCompMonetaire: '',
    societeDiGestion: '',
    libelle: '',
    prix: '',
    projectedActionStock: '',
    projectedObligataireStock: '',
    projectedMonetaireStock: '',
    projectedOrStock: '',
    currentActionStock: '',
    currentObligataireStock: '',
    currentMonetaireStock: '',
    currentOrStock: '',
    ISINCode: '',
    ReseauPlaceur: '',
    IndiceReplique: '',
    Liquidite: '',
    Actifs: '',
    Performance: '',
    TermsAndCondition: '',
    Propos: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  useEffect(() => {
    fetchParameters();
  }, []);

  const fetchParameters = async () => {
    setLoading(true);
    try {
      const response = await getAppParameters();
      const param = response.Data && response.Data.length > 0 ? response.Data[0] : null;

      if (param) {
        setFormData({
          noOfCompany: param.NoOfCompany || '',
          noOfAmwalySelectedCompany: param.NoOfAmwalySelectedCompany || '',
          NoOfCompanyMonetaire: param.NoOfCompanyMonetaire || '',
          NoOfAmwalySelCompMonetaire: param.NoOfAmwalySelCompMonetaire || '',
          societeDiGestion: param.MonPortfolioOrSocieteDiGestion || '',
          libelle: param.MonPortfolioOrLibelle || '',
          prix: param.MonPortfolioOrPrix || '',
          projectedActionStock: param.ProjectedActionStock || '',
          projectedObligataireStock: param.ProjectedObligataireStock || '',
          projectedMonetaireStock: param.ProjectedMonetaireStock || '',
          projectedOrStock: param.ProjectedOrStock || '',
          currentActionStock: param.CurrentActionStock || '',
          currentObligataireStock: param.CurrentObligataireStock || '',
          currentMonetaireStock: param.CurrentMonetaireStock || '',
          currentOrStock: param.CurrentOrStock || '',
          ISINCode: param.ISINCode || '',
          ReseauPlaceur: param.ReseauPlaceur || '',
          IndiceReplique: param.IndiceReplique || '',
          Liquidite: param.Liquidite || '',
          Actifs: param.Actifs || '',
          Performance: param.Performance || '',
          TermsAndCondition: param.TermsAndCondition || '',
          Propos: param.Propos || '',
        });
      }

      setIsDataLoaded(true);
    } catch (error) {
      toast.error('Failed to fetch parameters');
    } finally {
      setLoading(false);
    }
  };

  // VALIDATION
  const validateForm = () => {
    const newErrors = {};

    const validateNumber = (value) =>
      !value || isNaN(value) || parseFloat(value) < 0;

    if (validateNumber(formData.noOfCompany))
      newErrors.noOfCompany = 'Please enter a valid positive integer';

    if (validateNumber(formData.noOfAmwalySelectedCompany))
      newErrors.noOfAmwalySelectedCompany = 'Please enter a valid positive integer';

    if (validateNumber(formData.NoOfCompanyMonetaire))
      newErrors.NoOfCompanyMonetaire = 'Please enter a valid positive integer';

    if (validateNumber(formData.NoOfAmwalySelCompMonetaire))
      newErrors.NoOfAmwalySelCompMonetaire = 'Please enter a valid positive integer';

    if (!formData.societeDiGestion.trim())
      newErrors.societeDiGestion = 'Please enter Societe Di Gestion';

    if (!formData.libelle.trim())
      newErrors.libelle = 'Please enter Libelle';

    if (validateNumber(formData.prix))
      newErrors.prix = 'Please enter a valid positive number';

    if (validateNumber(formData.projectedActionStock))
      newErrors.projectedActionStock = 'Required';

    if (validateNumber(formData.projectedObligataireStock))
      newErrors.projectedObligataireStock = 'Required';

    if (validateNumber(formData.projectedMonetaireStock))
      newErrors.projectedMonetaireStock = 'Required';

    if (validateNumber(formData.projectedOrStock))
      newErrors.projectedOrStock = 'Required';

    const validateOptionalNumber = (value) => value && (isNaN(value) || parseFloat(value) < 0);

    if (validateOptionalNumber(formData.currentActionStock))
      newErrors.currentActionStock = 'Please enter a valid positive number';

    if (validateOptionalNumber(formData.currentObligataireStock))
      newErrors.currentObligataireStock = 'Please enter a valid positive number';

    if (validateOptionalNumber(formData.currentMonetaireStock))
      newErrors.currentMonetaireStock = 'Please enter a valid positive number';

    if (validateOptionalNumber(formData.currentOrStock))
      newErrors.currentOrStock = 'Please enter a valid positive number';

    if (!/^[a-zA-Z0-9]*$/.test(formData.ISINCode) || formData.ISINCode.length > 20)
      newErrors.ISINCode = 'Alphanumeric only, max 20 characters';

    if (!formData.ReseauPlaceur.trim())
      newErrors.ReseauPlaceur = 'Required';

    if (!formData.IndiceReplique.trim())
      newErrors.IndiceReplique = 'Required';

    if (!formData.Liquidite.trim())
      newErrors.Liquidite = 'Required';

    if (formData.Actifs && isNaN(formData.Actifs))
      newErrors.Actifs = 'Must be a numeric value';

    if (formData.Performance && isNaN(formData.Performance))
      newErrors.Performance = 'Must be numeric (decimals allowed)';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData({ ...formData, [name]: value });

    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      const payload = {
        NoOfCompany: parseInt(formData.noOfCompany),
        NoOfAmwalySelectedCompany: parseInt(formData.noOfAmwalySelectedCompany),
        NoOfCompanyMonetaire: parseInt(formData.NoOfCompanyMonetaire),
        NoOfAmwalySelCompMonetaire: parseInt(formData.NoOfAmwalySelCompMonetaire),
        MonPortfolioOrSocieteDiGestion: formData.societeDiGestion.trim(),
        MonPortfolioOrLibelle: formData.libelle.trim(),
        MonPortfolioOrPrix: parseFloat(formData.prix),
        ProjectedActionStock: parseInt(formData.projectedActionStock),
        ProjectedObligataireStock: parseInt(formData.projectedObligataireStock),
        ProjectedMonetaireStock: parseInt(formData.projectedMonetaireStock),
        ProjectedOrStock: parseInt(formData.projectedOrStock),
        CurrentActionStock: formData.currentActionStock ? parseInt(formData.currentActionStock) : null,
        CurrentObligataireStock: formData.currentObligataireStock ? parseInt(formData.currentObligataireStock) : null,
        CurrentMonetaireStock: formData.currentMonetaireStock ? parseInt(formData.currentMonetaireStock) : null,
        CurrentOrStock: formData.currentOrStock ? parseInt(formData.currentOrStock) : null,
        ISINCode: formData.ISINCode.trim(),
        ReseauPlaceur: formData.ReseauPlaceur.trim(),
        IndiceReplique: formData.IndiceReplique.trim(),
        Liquidite: formData.Liquidite.trim(),
        Actifs: formData.Actifs.trim(),
        Performance: formData.Performance.trim(),
        TermsAndCondition: formData.TermsAndCondition,
        Propos: formData.Propos,
      };

      const response = await upsertAppParameters(payload);

      toast.success(response.message || 'Parameters saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save parameters');
    } finally {
      setLoading(false);
    }
  };

  if (!isDataLoaded) {
    return <div className="text-center py-4">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      <p className="mt-2">Loading Parameters...</p>
    </div>;
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="card radius-12">
            <div className="card-header">
              <h4 className="text-xl fw-semibold text-primary-light mb-0">App Parameter</h4>
            </div>
            <div className="card-body p-24">
              <div className="card radius-12 mb-24">
                <div className="card-header">
                  <h6 className="mb-0">Information for Notations Menu</h6>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6 mb-4">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                        No Of Company <span className="text-danger-600">*</span>
                      </label>
                      <input
                        type="number"
                        name="noOfCompany"
                        value={formData.noOfCompany}
                        onChange={handleInputChange}
                        placeholder='Enter No Of Company'
                        className="form-control radius-8 border border-gray-300"
                      />
                      {errors.noOfCompany && <span className="text-danger-600">{errors.noOfCompany}</span>}
                    </div>

                    <div className="col-md-6 mb-4">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                        No Of Amwaly Selected Company <span className="text-danger-600">*</span>
                      </label>
                      <input
                        type="number"
                        name="noOfAmwalySelectedCompany"
                        value={formData.noOfAmwalySelectedCompany}
                        onChange={handleInputChange}
                        placeholder='Enter No Of Amwaly Selected Company'
                        className="form-control radius-8 border border-gray-300"
                      />
                      {errors.noOfAmwalySelectedCompany && <span className="text-danger-600">{errors.noOfAmwalySelectedCompany}</span>}
                    </div>

                    <div className="col-md-6 mb-4">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                        No. Of Company (For Monetaire) <span className="text-danger-600">*</span>
                      </label>
                      <input
                        type="number"
                        name="NoOfCompanyMonetaire"
                        value={formData.NoOfCompanyMonetaire}
                        onChange={handleInputChange}
                        placeholder='Enter No Of Company (For Monetaire)'
                        className="form-control radius-8 border border-gray-300"
                      />
                      {errors.NoOfCompanyMonetaire && <span className="text-danger-600">{errors.NoOfCompanyMonetaire}</span>}
                    </div>

                    <div className="col-md-6 mb-4">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                        No. Of Amwaly Selected Company (For Monetaire) <span className="text-danger-600">*</span>
                      </label>
                      <input
                        type="number"
                        name="NoOfAmwalySelCompMonetaire"
                        value={formData.NoOfAmwalySelCompMonetaire}
                        onChange={handleInputChange}
                        placeholder='Enter No Of Amwaly Selected Company (For Monetaire)'
                        className="form-control radius-8 border border-gray-300"
                      />
                      {errors.NoOfAmwalySelCompMonetaire && <span className="text-danger-600">{errors.NoOfAmwalySelCompMonetaire}</span>}
                    </div>

                    <div className="col-md-6 mb-4">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                        Societe Di Gestion (For Or Option in Mon Protefeuille) <span className="text-danger-600">*</span>
                      </label>
                      <input
                        type="text"
                        name="societeDiGestion"
                        value={formData.societeDiGestion}
                        onChange={handleInputChange}
                        placeholder='Enter Societe Di Gestion'
                        className="form-control radius-8 border border-gray-300"
                      />
                      {errors.societeDiGestion && <span className="text-danger-600">{errors.societeDiGestion}</span>}
                    </div>

                    <div className="col-md-6 mb-4">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                        Libelle (For Or Option in Mon Protefeuille) <span className="text-danger-600">*</span>
                      </label>
                      <input
                        type="text"
                        name="libelle"
                        value={formData.libelle}
                        onChange={handleInputChange}
                        placeholder='Enter Libelle'
                        className="form-control radius-8 border border-gray-300"
                      />
                      {errors.libelle && <span className="text-danger-600">{errors.libelle}</span>}
                    </div>

                    <div className="col-md-12 mb-4">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                        Prix (For Or Option in Mon Protefeuille) <span className="text-danger-600">*</span>
                      </label>
                      <input
                        type="number"
                        name="prix"
                        value={formData.prix}
                        onChange={handleInputChange}
                        placeholder='Enter Prix (e.g., 123.45)'
                        className="form-control radius-8 border border-gray-300"
                      />
                      {errors.prix && <span className="text-danger-600">{errors.prix}</span>}
                    </div>

                    <div className="col-md-6 mb-4">
                      <label className="form-label">ISIN Code</label>
                      <input
                        type="text"
                        name="ISINCode"
                        value={formData.ISINCode}
                        onChange={handleInputChange}
                        className="form-control radius-8 border border-gray-300"
                        maxLength={20}
                      />
                      {errors.ISINCode && <span className="text-danger-600">{errors.ISINCode}</span>}
                    </div>

                    <div className="col-md-6 mb-4">
                      <label className="form-label">Reseau Placeur</label>
                      <input
                        type="text"
                        name="ReseauPlaceur"
                        value={formData.ReseauPlaceur}
                        onChange={handleInputChange}
                        className="form-control radius-8 border border-gray-300"
                      />
                      {errors.ReseauPlaceur && <span className="text-danger-600">{errors.ReseauPlaceur}</span>}
                    </div>

                    <div className="col-md-6 mb-4">
                      <label className="form-label">Indice Replique</label>
                      <input
                        type="text"
                        name="IndiceReplique"
                        value={formData.IndiceReplique}
                        onChange={handleInputChange}
                        className="form-control radius-8 border border-gray-300"
                      />
                      {errors.IndiceReplique && <span className="text-danger-600">{errors.IndiceReplique}</span>}
                    </div>

                    <div className="col-md-6 mb-4">
                      <label className="form-label">Liquidite</label>
                      <input
                        type="text"
                        name="Liquidite"
                        value={formData.Liquidite}
                        onChange={handleInputChange}
                        className="form-control radius-8 border border-gray-300"
                      />
                      {errors.Liquidite && <span className="text-danger-600">{errors.Liquidite}</span>}
                    </div>

                    <div className="col-md-6 mb-4">
                      <label className="form-label">Actifs</label>
                      <input
                        type="text"
                        name="Actifs"
                        value={formData.Actifs}
                        onChange={handleInputChange}
                        className="form-control radius-8 border border-gray-300"
                      />
                      {errors.Actifs && <span className="text-danger-600">{errors.Actifs}</span>}
                    </div>

                    <div className="col-md-6 mb-4">
                      <label className="form-label">Performance</label>
                      <input
                        type="text"
                        name="Performance"
                        value={formData.Performance}
                        onChange={handleInputChange}
                        className="form-control radius-8 border border-gray-300"
                      />
                      {errors.Performance && <span className="text-danger-600">{errors.Performance}</span>}
                    </div>
                  </div>
                </div>
              </div>

              <div className="card radius-12 mb-24">
                <div className="card-header">
                  <h6 className="mb-0">Information for Recommendations</h6>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6 mb-4">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                        Current Action Stock
                      </label>
                      <input
                        type="number"
                        name="currentActionStock"
                        value={formData.currentActionStock}
                        onChange={handleInputChange}
                        placeholder='Enter Current Action Stock'
                        className="form-control radius-8 border border-gray-300"
                      />
                      {errors.currentActionStock && <span className="text-danger-600">{errors.currentActionStock}</span>}
                    </div>

                    <div className="col-md-6 mb-4">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                        Current Obligataire Stock
                      </label>
                      <input
                        type="number"
                        name="currentObligataireStock"
                        value={formData.currentObligataireStock}
                        onChange={handleInputChange}
                        placeholder='Enter Current Obligataire Stock'
                        className="form-control radius-8 border border-gray-300"
                      />
                      {errors.currentObligataireStock && <span className="text-danger-600">{errors.currentObligataireStock}</span>}
                    </div>

                    <div className="col-md-6 mb-4">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                        Current Monetaire Stock
                      </label>
                      <input
                        type="number"
                        name="currentMonetaireStock"
                        value={formData.currentMonetaireStock}
                        onChange={handleInputChange}
                        placeholder='Enter Current Monetaire Stock'
                        className="form-control radius-8 border border-gray-300"
                      />
                      {errors.currentMonetaireStock && <span className="text-danger-600">{errors.currentMonetaireStock}</span>}
                    </div>

                    <div className="col-md-6 mb-4">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                        Current Or Stock
                      </label>
                      <input
                        type="number"
                        name="currentOrStock"
                        value={formData.currentOrStock}
                        onChange={handleInputChange}
                        placeholder='Enter Current Or Stock'
                        className="form-control radius-8 border border-gray-300"
                      />
                      {errors.currentOrStock && <span className="text-danger-600">{errors.currentOrStock}</span>}
                    </div>

                    <div className="col-md-6 mb-4">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                        Target Action Stock <span className="text-danger-600">*</span>
                      </label>
                      <input
                        type="number"
                        name="projectedActionStock"
                        value={formData.projectedActionStock}
                        onChange={handleInputChange}
                        placeholder='Enter Target Action Stock'
                        className="form-control radius-8 border border-gray-300"
                      />
                      {errors.projectedActionStock && <span className="text-danger-600">{errors.projectedActionStock}</span>}
                    </div>

                    <div className="col-md-6 mb-4">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                         Target Obligataire Stock <span className="text-danger-600">*</span>
                      </label>
                      <input
                        type="number"
                        name="projectedObligataireStock"
                        value={formData.projectedObligataireStock}
                        onChange={handleInputChange}
                        placeholder='Enter Target Obligataire Stock'
                        className="form-control radius-8 border border-gray-300"
                      />
                      {errors.projectedObligataireStock && <span className="text-danger-600">{errors.projectedObligataireStock}</span>}
                    </div>

                    <div className="col-md-6 mb-4">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                        Target Monetaire Stock <span className="text-danger-600">*</span>
                      </label>
                      <input
                        type="number"
                        name="projectedMonetaireStock"
                        value={formData.projectedMonetaireStock}
                        onChange={handleInputChange}
                        placeholder='Enter Target Monetaire Stock'
                        className="form-control radius-8 border border-gray-300"
                      />
                      {errors.projectedMonetaireStock && <span className="text-danger-600">{errors.projectedMonetaireStock}</span>}
                    </div>

                    <div className="col-md-6 mb-4">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                         Target Or Stock <span className="text-danger-600">*</span>
                      </label>
                      <input
                        type="number"
                        name="projectedOrStock"
                        value={formData.projectedOrStock}
                        onChange={handleInputChange}
                        placeholder='Enter Target Or Stock'
                        className="form-control radius-8 border border-gray-300"
                      />
                      {errors.projectedOrStock && <span className="text-danger-600">{errors.projectedOrStock}</span>}
                    </div>
                  </div>
                </div>
              </div>

              <div className="card radius-12">
                <div className="card-header">
                  <h6 className="mb-0">Other Information</h6>
                </div>
                <div className="card-body">
                  <div className="mb-4">
                    <label className="form-label">Terms And Condition</label>
                    <textarea
                      name="TermsAndCondition"
                      value={formData.TermsAndCondition}
                      onChange={handleInputChange}
                      rows={4}
                      className="form-control radius-8 border border-gray-300"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label">Propos</label>
                    <textarea
                      name="Propos"
                      value={formData.Propos}
                      onChange={handleInputChange}
                      rows={3}
                      className="form-control radius-8 border border-gray-300"
                    />
                  </div>
                </div>
              </div>

              <div className="d-flex align-items-center justify-content-center gap-3 mt-5">
                <button
                  type="button"
                  className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? 'Processing...' : 'Save'}
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppParameters;